import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
// import Footer from "../../components/Footer";
import { getImage, fallbackImages } from "../../config/instagramImages";

interface Dish {
  name: string;
  description: string;
  price: string;
  image: string;
  popular?: boolean;
  chef?: string;
}

const MenuPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const menuCategories = [
    {
      id: "appetizers",
      name: "Appetizers",
      description: "Start your journey with our carefully crafted starters",
      dishes: [
        {
          name: "Truffle Arancini",
          description:
            "Crispy risotto balls with black truffle and mozzarella, served with truffle aioli",
          price: "₦18,000",
          image: getImage(
            "/images/instagram/truffle-arancini.jpg",
            fallbackImages.truffleArancini
          ),
          popular: true,
        },
        {
          name: "Burrata Caprese",
          description:
            "Fresh burrata with heirloom tomatoes, basil, aged balsamic, and extra virgin olive oil",
          price: "₦16,000",
          image: getImage(
            "/images/instagram/burrata-caprese.jpg",
            fallbackImages.burrataCaprese
          ),
        },
        {
          name: "Lobster Bisque",
          description:
            "Rich and creamy lobster soup with cognac and crème fraîche",
          price: "₦22,000",
          image: getImage(
            "/images/instagram/lobster-bisque.jpg",
            fallbackImages.lobsterBisque
          ),
        },
      ] as Dish[],
    },
    {
      id: "main-courses",
      name: "Main Courses",
      description: "Our signature dishes crafted with passion and precision",
      dishes: [
        {
          name: "Wagyu Beef Tenderloin",
          description:
            "Grade A5 Japanese Wagyu with roasted garlic mashed potatoes, seasonal vegetables, and red wine reduction",
          price: "₦85,000",
          image: getImage(
            "/images/instagram/wagyu-beef.jpg",
            fallbackImages.wagyuBeef
          ),
          popular: true,
          chef: "Chef's Special",
        },
        {
          name: "Pan-Seared Sea Bass",
          description:
            "Mediterranean sea bass with saffron risotto, grilled asparagus, and lemon butter sauce",
          price: "₦42,000",
          image: getImage(
            "/images/instagram/sea-bass.jpg",
            fallbackImages.seaBass
          ),
        },
        {
          name: "Duck Confit",
          description:
            "Traditional French duck confit with pommes purée, haricots verts, and cherry reduction",
          price: "₦38,000",
          image: getImage(
            "/images/instagram/duck-confit.jpg",
            fallbackImages.duckConfit
          ),
        },
        {
          name: "Truffle Tagliatelle",
          description:
            "House-made tagliatelle with black truffle, parmesan, and brown butter sauce",
          price: "₦32,000",
          image: getImage(
            "/images/instagram/truffle-pasta.jpg",
            fallbackImages.trufflePasta
          ),
        },
      ] as Dish[],
    },
    {
      id: "desserts",
      name: "Desserts",
      description: "Sweet endings to your perfect meal",
      dishes: [
        {
          name: "Chocolate Soufflé",
          description:
            "Warm chocolate soufflé with vanilla bean ice cream and chocolate sauce",
          price: "₦16,000",
          image: getImage(
            "/images/instagram/chocolate-souffle.jpg",
            fallbackImages.chocolateSouffle
          ),
          popular: true,
        },
        {
          name: "Crème Brûlée",
          description:
            "Classic vanilla custard with caramelized sugar and fresh berries",
          price: "₦14,000",
          image: getImage(
            "/images/instagram/creme-brulee.jpg",
            fallbackImages.cremeBrulee
          ),
        },
        {
          name: "Tiramisu",
          description:
            "Traditional Italian dessert with espresso-soaked ladyfingers and mascarpone cream",
          price: "₦15,000",
          image: getImage(
            "/images/instagram/tiramisu.jpg",
            fallbackImages.tiramisu
          ),
        },
      ] as Dish[],
    },
    {
      id: "wine",
      name: "Wine & Beverages",
      description:
        "Carefully curated selection of fine wines and craft cocktails",
      dishes: [
        {
          name: "Château Margaux 2015",
          description:
            "Bordeaux, France - Full-bodied with notes of black fruit, tobacco, and leather",
          price: "₦450,000",
          image: getImage(
            "/images/instagram/chateau-margaux.jpg",
            fallbackImages.chateauMargaux
          ),
        },
        {
          name: "Dom Pérignon 2012",
          description:
            "Champagne, France - Elegant bubbles with citrus and brioche notes",
          price: "₦380,000",
          image: getImage(
            "/images/instagram/dom-perignon.jpg",
            fallbackImages.domPerignon
          ),
        },
        {
          name: "King's Old Fashioned",
          description:
            "Bourbon, bitters, sugar cube, and orange peel - our signature cocktail",
          price: "₦18,000",
          image: getImage(
            "/images/instagram/old-fashioned.jpg",
            fallbackImages.oldFashioned
          ),
        },
      ] as Dish[],
    },
  ];

  const allDishes = menuCategories.flatMap((category) =>
    category.dishes.map((dish) => ({ ...dish, category: category.name }))
  );

  const filteredDishes =
    activeCategory === "all"
      ? allDishes
      : allDishes.filter(
          (dish) =>
            dish.category ===
            menuCategories.find((cat) => cat.id === activeCategory)?.name
        );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url(${getImage(
              "/images/instagram/hero-background.jpg",
              fallbackImages.hero
            )})`,
          }}
        ></div>

        <div className="relative z-10 container-custom text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Our Menu
          </h1>
          <p className="text-xl md:text-2xl text-amber-100 max-w-4xl mx-auto mb-8">
            Discover our carefully crafted dishes made with the finest
            ingredients and culinary expertise
          </p>
          <div className="flex justify-center">
            <div className="bg-white/10 backdrop-blur-md rounded-full px-8 py-3">
              <span className="text-white font-medium">
                Seasonal Menu • Updated Weekly
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === "all"
                  ? "bg-amber-600 text-white shadow-lg"
                  : "bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-600"
              }`}
            >
              All Dishes
            </button>
            {menuCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === category.id
                    ? "bg-amber-600 text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-600"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid gap-8">
            {menuCategories.map((category) => (
              <div
                key={category.id}
                className={`${
                  activeCategory !== "all" && activeCategory !== category.id
                    ? "hidden"
                    : ""
                }`}
              >
                <div className="text-center mb-12">
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                    {category.name}
                  </h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    {category.description}
                  </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                  {category.dishes.map((dish, index) => (
                    <div
                      key={index}
                      className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={dish.image}
                          alt={dish.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                        {/* Popular Badge */}
                        {dish.popular && (
                          <div className="absolute top-4 left-4">
                            <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                              Popular
                            </span>
                          </div>
                        )}

                        {/* Chef's Special Badge */}
                        {dish.chef && (
                          <div className="absolute top-4 right-4">
                            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                              {dish.chef}
                            </span>
                          </div>
                        )}

                        {/* Price */}
                        <div className="absolute bottom-4 right-4">
                          <span className="bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-full text-lg font-bold">
                            {dish.price}
                          </span>
                        </div>
                      </div>

                      <div className="p-8">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-2xl font-bold text-gray-900">
                            {dish.name}
                          </h3>
                        </div>
                        <p className="text-gray-600 text-lg leading-relaxed">
                          {dish.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Section */}
      <section className="section-padding bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Chef's Recommendations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our culinary team's personal favorites, crafted with passion and
              expertise
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Truffle Arancini",
                description:
                  "Our most popular appetizer, featuring imported black truffle",
                price: "₦18,000",
                image: getImage(
                  "/images/instagram/truffle-arancini.jpg",
                  fallbackImages.truffleArancini
                ),
              },
              {
                name: "Wagyu Beef Tenderloin",
                description:
                  "The crown jewel of our menu, featuring Grade A5 Japanese Wagyu",
                price: "₦85,000",
                image: getImage(
                  "/images/instagram/wagyu-beef.jpg",
                  fallbackImages.wagyuBeef
                ),
              },
              {
                name: "Chocolate Soufflé",
                description: "A perfect ending to your dining experience",
                price: "₦16,000",
                image: getImage(
                  "/images/instagram/chocolate-souffle.jpg",
                  fallbackImages.chocolateSouffle
                ),
              },
            ].map((dish, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="h-48 rounded-xl overflow-hidden mb-6">
                  <img
                    src={dish.image}
                    alt={dish.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {dish.name}
                </h3>
                <p className="text-gray-600 mb-4">{dish.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-amber-600">
                    {dish.price}
                  </span>
                  <span className="text-amber-600 font-medium">
                    Chef's Pick
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-gray-900 to-gray-800">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Experience Our Cuisine?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Make a reservation and join us for an unforgettable dining
            experience that will tantalize your taste buds
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 inline-block"
            >
              Reserve Your Table
            </Link>
            <Link
              to="/about"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold py-4 px-8 rounded-lg transition-all duration-300"
            >
              Learn About Our Story
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MenuPage;
